/* eslint-disable no-restricted-exports */
/* eslint-disable prettier/prettier */
/* axiosServices.ts */
import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  AxiosInstance,
} from "axios";
import { toast } from "react-toastify";
import enviroment from "../enviroments";
import { obterTokenConexao } from "./conexaoAuth";
import signOut from "./signOut";
import {
  handleRefreshToken,
  TOKEN_KEY,
  getCurrentToken,
} from "../hooks/useRefreshToken";

interface TokenData {
  token?: string;
  refreshToken?: string;
  expires_in?: string;
  tokenAD?: string;
}

interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

let refreshFailCount = 0;
const MAX_REFRESH_FAILURES = 3;
let lastRefreshAttempt = 0;
const MIN_REFRESH_INTERVAL = 5000; // 5 segundos

/**
 * Adiciona um callback à fila de subscribers
 * @param callback Função a ser chamada quando o token for renovado
 */
function subscribeTokenRefresh(callback: (token: string) => void): void {
  refreshSubscribers.push(callback);
}

/**
 * Executa todos os callbacks registrados passando o novo token
 * @param token O novo token obtido após o refresh
 */
function onTokenRefreshed(token: string): void {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

function clearRefreshSubscribers(): void {
  refreshSubscribers.forEach((callback) => callback(""));
  refreshSubscribers = [];
}

/**
 * Cria e configura uma instância do axios
 * @param baseURL URL base para requisições
 * @param clientId ID do cliente para identificação (cc)
 * @returns Instância configurada do axios
 */
function createAxiosInstance(baseURL: string, clientId: string): AxiosInstance {
  const config: AxiosRequestConfig = {
    baseURL,
    headers: { "Content-Type": "application/json" },
    params: { cc: clientId },
  };

  return axios.create(config);
}

const axiosDefault = createAxiosInstance(
  enviroment.BASE_URL || "",
  "6909b8b9-7dfc-40e2-af74-3d0b25c65248"
);

const instanceConexao = createAxiosInstance(
  enviroment.CONEXAO_URL || "",
  "056d1622-4ade-42da-ab38-7126dbca8e0e"
);

/**
 * Configura o interceptor de request para adicionar o token
 * @param instance Instância do axios a ser configurada
 */
function setupRequestInterceptor(instance: AxiosInstance): void {
  instance.interceptors.request.use(
    (config) => {
      const tokenString = localStorage.getItem(TOKEN_KEY);
      if (tokenString) {
        try {
          const { token } = JSON.parse(tokenString) as TokenData;
          if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.error("[Axios] Erro ao processar token:", error);
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
}

/**
 * Configura o interceptor de resposta para tratar erros e refresh token
 * @param instance Instância do axios a ser configurada
 */
function setupResponseInterceptor(instance: AxiosInstance): void {
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      // Se não for erro 401 ou não tiver config, rejeita diretamente
      if (error.response?.status !== 401 || !error.config) {
        return Promise.reject(error);
      }

      const originalConfig = error.config as ExtendedAxiosRequestConfig;

      if (originalConfig._retry) {
        console.log(
          "[Axios] Já tentamos refresh para esta request, não tentando novamente"
        );
        return Promise.reject(error);
      }

      const originalRequest = { ...originalConfig, _retry: true };

      const now = Date.now();
      if (now - lastRefreshAttempt < MIN_REFRESH_INTERVAL) {
        console.log("[Axios] Refresh recente, aguardando...");

        if (isRefreshing) {
          try {
            const newToken = await new Promise<string>((resolve, reject) => {
              subscribeTokenRefresh(resolve);
            });

            if (newToken) {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
              }
              return axios(originalRequest);
            }
            return Promise.reject(error);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }

        const currentToken = getCurrentToken();
        if (currentToken) {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${currentToken}`;
          }
          return axios(originalRequest);
        }

        return Promise.reject(error);
      }

      if (isRefreshing) {
        try {
          console.log("[Axios] Refresh em andamento, adicionando à fila");
          const newToken = await new Promise<string>((resolve, reject) => {
            subscribeTokenRefresh(resolve);
          });

          if (!newToken) {
            console.log("[Axios] Recebeu token vazio da fila");
            return Promise.reject(error);
          }

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          return axios(originalRequest);
        } catch (refreshError) {
          console.error("[Axios] Erro ao esperar token da fila:", refreshError);
          return Promise.reject(refreshError);
        }
      }

      // Inicia o processo de refresh
      isRefreshing = true;
      lastRefreshAttempt = now;

      try {
        if (refreshFailCount >= MAX_REFRESH_FAILURES) {
          console.log("[Axios] Muitas falhas consecutivas, forçando logout");
          throw new Error("Muitas falhas de refresh consecutivas");
        }

        const refreshSucceeded = await handleRefreshToken();

        if (refreshSucceeded) {
          refreshFailCount = 0;

          const tokenString = localStorage.getItem(TOKEN_KEY);
          if (!tokenString) {
            throw new Error("Token não encontrado após refresh");
          }

          const parsedToken = JSON.parse(tokenString) as TokenData;
          if (!parsedToken.token) {
            throw new Error("Token inválido após refresh");
          }

          onTokenRefreshed(parsedToken.token);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${parsedToken.token}`;
          }
          console.log("[Axios] Refresh bem-sucedido, retentando request");
          return axios(originalRequest);
        }

        refreshFailCount += 1;

        clearRefreshSubscribers();

        console.log("[Axios] Refresh falhou, executando logout");
        toast.error("Sua sessão expirou. Por favor, faça login novamente.");

        setTimeout(() => signOut(), 1000);

        return Promise.reject(new Error("Falha ao renovar sessão"));
      } catch (refreshError) {
        console.error("[Axios] Erro no refresh token:", refreshError);

        refreshFailCount += 1;

        clearRefreshSubscribers();

        toast.error("Sessão expirada. Por favor, faça login novamente.");
        setTimeout(() => signOut(), 1000);

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
}

setupRequestInterceptor(axiosDefault);
setupResponseInterceptor(axiosDefault);

// Os interceptors acima anexam o token da **identity do Sellit**. Aplicá-los ao instanceConexao
// seria errado: o Conexão não valida aquele token. Por isso seguem desligados aqui, e o Conexão
// ganha um interceptor próprio, logo abaixo.
//
// setupRequestInterceptor(instanceConexao);
// setupResponseInterceptor(instanceConexao);

/**
 * Um `Authorization` que existe mas não serve — `"Bearer undefined"` ou `"Bearer null"`.
 *
 * Tratá-lo como ausente é o que permite o interceptor abaixo anexar o token real.
 */
function authorizationInutilizavel(valor: unknown): boolean {
  return (
    typeof valor !== "string" ||
    /^bearer\s*(undefined|null)?$/i.test(valor.trim())
  );
}

/** Anexa o JWT do Conexão às chamadas de `instanceConexao` que não trazem `Authorization` utilizável. */
instanceConexao.interceptors.request.use(async (config) => {
  if (!authorizationInutilizavel(config.headers?.Authorization)) return config;

  try {
    // Normalmente a troca já aconteceu no login, em `services/signIn`; aqui é a rede de segurança.
    const token = await obterTokenConexao();
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch (erro) {
    // Segue sem token — o 401 do Conexão é tratado pelo chamador —, mas registrando.
    console.error("[conexaoAuth] Não foi possível anexar o token do Conexão:", erro);
  }

  return config;
});

export { axiosDefault as default, instanceConexao };
